import {
  Dropdown,
  Icon,
  tailwindOverride,
  Tooltip,
} from '@getsynapse/design-system';
import { FC, ReactNode, MutableRefObject, useEffect } from 'react';
import { Option } from 'utils/customTypes';
import { useScrollLock } from '../StatusTableRowCell/hooks';
import intl from 'react-intl-universal';
import { REQUEST_STATUSES } from 'utils/constants/request';

export type DropdownOption = Option & { disabled?: boolean; message?: string };

interface InlineDropdownProps {
  value: string;
  customizedValue: (value: string) => ReactNode;
  selectedOption: DropdownOption[];
  options: DropdownOption[];
  onChange: (value: string) => void;
  toggleDropdownRef?: MutableRefObject<(() => void) | null>;
  triggerRef?: MutableRefObject<HTMLDivElement | null>;
  autoOpen?: boolean;
  saveTriggerRef?: MutableRefObject<(() => void) | null>;
  renderOption?: (
    option: DropdownOption,
    selected: boolean,
    selectOption: () => void,
    otherProps: Record<string, any>
  ) => ReactNode;
  dataAttributePrefix?: string;
}

export const InlineDropdown: FC<InlineDropdownProps> = ({
  value,
  customizedValue,
  selectedOption,
  options,
  onChange,
  toggleDropdownRef,
  triggerRef,
  autoOpen,
  saveTriggerRef,
  renderOption,
  dataAttributePrefix = 'dropdown',
}) => {
  useScrollLock(true);

  const defaultRenderOption = (
    option: Option,
    selected: boolean,
    selectOption: () => void,
    { className, ...otherProps }: Record<string, any>
  ) => {
    const dropdownOption = option as DropdownOption;
    const indexInOptions = options.findIndex(
      (o) => o.value === dropdownOption.value
    );

    const enhancedOtherProps = {
      ...otherProps,
      [`data-${dataAttributePrefix}-option-index`]: indexInOptions.toString(),
    };

    if (renderOption) {
      return renderOption(
        dropdownOption,
        selected,
        selectOption,
        enhancedOtherProps
      );
    }

    // Default rendering
    const disabled = !!dropdownOption.disabled;
    const disabledClass = disabled
      ? 'text-neutral-lighter cursor-not-allowed'
      : '';

    const listItem = (
      <li
        {...enhancedOtherProps}
        data-option='true'
        data-disabled={disabled ? 'true' : 'false'}
        onClick={() => {
          if (!disabled) {
            selectOption();
          }
        }}
        onKeyDown={(event) => {
          const { key } = event;

          if (key === 'ArrowDown' || key === 'ArrowUp') {
            event.preventDefault();
            event.stopPropagation();

            if (disabled) return;

            moveFocusBetweenOptions(
              event.currentTarget,
              key === 'ArrowDown' ? 'down' : 'up'
            );
            return;
          }

          if (key === 'Enter' || key === ' ') {
            event.preventDefault();
            event.stopPropagation();

            if (!disabled) {
              selectOption();
            }
          }
        }}
        tabIndex={-1}
        role='option'
        aria-selected={selected}
        className={tailwindOverride(
          'font-body text-base truncate group px-4 py-2 focus-visible:border-0 focus-visible:ring-0 focus-visible:outline-none',
          {
            'cursor-pointer text-neutral-black hover:text-neutral-dark hover:shadow-list-item-hover hover:bg-neutral-lighter-two focus-visible:bg-neutral-lighter-two focus-visible:text-neutral-dark focus-visible:shadow-list-item-hover':
              !disabled,
            'text-neutral-white bg-primary hover:text-neutral-white hover:bg-primary focus-visible:bg-primary focus-visible:text-neutral-white':
              selected && !disabled,
          },
          disabledClass
        )}
      >
        {dropdownOption.label}
      </li>
    );

    if (disabled) {
      return (
        <Tooltip
          className='w-full'
          showPopper
          usePortal
          openMode='hover1'
          timeout={0}
          trigger={listItem}
          position='topCenter'
          contentProps={{
            className: 'z-50 px-3 py-2 text-sm',
          }}
        >
          {intl.get(
            value === REQUEST_STATUSES.PENDING_CANCELLATION &&
              dropdownOption.value !== REQUEST_STATUSES.CANCELED
              ? 'REQUESTS_LIST_PAGE.DECLINE_TOOLTIP_MESSAGE.PENDING_CANCELLATION'
              : 'REQUESTS_LIST_PAGE.DECLINE_TOOLTIP_MESSAGE.ONLY_OWNER_CAN_SET',
            { action: dropdownOption?.label }
          )}
        </Tooltip>
      );
    }

    return listItem;
  };

  const moveFocusFromTrigger = (
    triggerEl: HTMLElement | null,
    direction: 'up' | 'down',
    selectedValue?: string
  ) => {
    if (!triggerEl) return;

    const root = triggerEl.closest(
      '[data-dropdown-root="true"]'
    ) as HTMLElement | null;
    if (!root) return;

    const enabledIndices = options
      .map((opt, idx) => (!opt.disabled ? idx : -1))
      .filter((idx) => idx !== -1);

    if (!enabledIndices.length) return;

    let targetIndexInOptions: number;

    if (!selectedValue) {
      targetIndexInOptions =
        direction === 'down'
          ? enabledIndices[0]
          : enabledIndices[enabledIndices.length - 1];
    } else {
      const selectedIdxInOptions = options.findIndex(
        (o) => o.value === selectedValue
      );
      const selectedIdxInEnabled = enabledIndices.indexOf(selectedIdxInOptions);

      if (selectedIdxInEnabled === -1) {
        targetIndexInOptions =
          direction === 'down'
            ? enabledIndices[0]
            : enabledIndices[enabledIndices.length - 1];
      } else {
        const nextIdxInEnabled =
          direction === 'down'
            ? selectedIdxInEnabled + 1
            : selectedIdxInEnabled - 1;

        if (nextIdxInEnabled < 0 || nextIdxInEnabled >= enabledIndices.length) {
          targetIndexInOptions = enabledIndices[selectedIdxInEnabled];
        } else {
          targetIndexInOptions = enabledIndices[nextIdxInEnabled];
        }
      }
    }

    const targetNode = root.querySelector<HTMLElement>(
      `[data-${dataAttributePrefix}-option-index="${targetIndexInOptions}"]`
    );
    if (targetNode) {
      targetNode.focus();
    }
  };

  const moveFocusBetweenOptions = (
    current: HTMLElement,
    direction: 'up' | 'down'
  ) => {
    const root = current.closest(
      '[data-dropdown-root="true"]'
    ) as HTMLElement | null;
    if (!root) return;

    const allNodes = Array.from(
      root.querySelectorAll<HTMLElement>('[data-option="true"]')
    );
    const enabledNodes = allNodes.filter(
      (node) => node.dataset.disabled !== 'true'
    );
    if (!enabledNodes.length) return;

    const currentIndex = enabledNodes.findIndex((n) => n === current);
    if (currentIndex === -1) return;

    const nextIndex =
      direction === 'down' ? currentIndex + 1 : currentIndex - 1;

    if (nextIndex < 0 || nextIndex >= enabledNodes.length) return;

    enabledNodes[nextIndex].focus();
  };

  useEffect(() => {
    if (autoOpen && toggleDropdownRef?.current) {
      toggleDropdownRef.current();
      if (triggerRef?.current) {
        triggerRef.current.focus();
      }
    }
  }, [autoOpen, toggleDropdownRef, triggerRef]);

  return (
    <div className='w-full' data-dropdown-root='true'>
      <Dropdown
        value={value}
        values={selectedOption}
        onChange={(option) => {
          onChange(option.value);
        }}
        options={options}
        renderOption={(
          option,
          selected,
          selectOption,
          { className, ...otherProps }
        ) => {
          return (
            <div className='min-w-40'>
              {defaultRenderOption(option, selected, selectOption, {
                className,
                ...otherProps,
              })}
            </div>
          );
        }}
        renderTrigger={({
          onToggleDropdown,
          selectedOptions,
          isDropdownExpanded,
          props,
        }) => {
          const triggerProps =
            (props as React.HTMLAttributes<HTMLDivElement> & {
              className?: string;
            }) || {};

          const {
            className: triggerClassName,
            onKeyDown: originalOnKeyDown,
            onClick: originalOnClick,
            ...restTriggerProps
          } = triggerProps;

          if (toggleDropdownRef) {
            toggleDropdownRef.current = onToggleDropdown;
          }

          const selectedValue = selectedOptions?.[0]?.value || value || '';

          const handleTriggerKeyDown = (
            event: React.KeyboardEvent<HTMLDivElement>
          ) => {
            const { key } = event;

            if (key === 'ArrowDown' || key === 'ArrowUp') {
              event.preventDefault();
              event.stopPropagation();
              moveFocusFromTrigger(
                event.currentTarget,
                key === 'ArrowDown' ? 'down' : 'up',
                selectedValue
              );
              return;
            }

            originalOnKeyDown?.(event);
          };

          const handleTriggerClick = (
            event: React.MouseEvent<HTMLDivElement>
          ) => {
            if (!isDropdownExpanded) {
              return originalOnClick?.(event);
            }

            originalOnClick?.(event);
            saveTriggerRef?.current?.();
          };

          return (
            <div
              {...restTriggerProps}
              ref={triggerRef}
              onClick={handleTriggerClick}
              onKeyDown={handleTriggerKeyDown}
              className={tailwindOverride(
                'cursor-pointer flex items-center justify-between border rounded ml-1 p-2 outline-none focus:outline-none focus-visible:outline-none',
                triggerClassName
              )}
            >
              {selectedValue ? customizedValue(selectedValue) : ''}
              <span title='' aria-hidden='true' className='inline-flex'>
                <Icon
                  name={
                    isDropdownExpanded
                      ? 'caret-up-outline'
                      : 'caret-down-outline'
                  }
                  className='pointer-events-none'
                />
              </span>
            </div>
          );
        }}
        className='w-full'
        popperDiffWidth={true}
      />
    </div>
  );
};
